import React from "react";
import { IoIosArrowDown, IoIosArrowUp } from "react-icons/io";
import { FiArrowDown, FiArrowUp, FiCheck } from "react-icons/fi";
import {
  Box,
  Flex,
  Icon,
  Portal,
  Text,
  useColorModeValue,
} from "@chakra-ui/react";
import { theme } from "src/styles/theme";

export type OrdenarPor =
  | "fase"
  | "nomedaconta"
  // | "nomeempreendimento"
  | "sla"
  | "ranking"
  | "criadordaoportunidade";

export type TipoOrdenacao = "asc" | "desc";

interface SortOption {
  label: string;
  value: OrdenarPor;
}

const sortOptions: SortOption[] = [
  { label: "Nome do cliente", value: "nomedaconta" },
  { label: "Fase", value: "fase" },
  // { label: "Empreendimento", value: "nomeempreendimento" },
  { label: "Última atualização", value: "sla" },
  { label: "Ranking", value: "ranking" },
  { label: "Criador da oportunidade", value: "criadordaoportunidade" },
];

interface IInputSortBy {
  ordenarPor: OrdenarPor;
  tipoOrdenacao: TipoOrdenacao;
  onChange: (ordenarPor: OrdenarPor, tipoOrdenacao: TipoOrdenacao) => void;
  widht?: string;
  widhtMobile?: string;
}

const InputSortBy = ({
  ordenarPor,
  tipoOrdenacao,
  onChange,
  widht,
  widhtMobile,
}: IInputSortBy) => {
  const wrapperRef = React.useRef<HTMLDivElement>(null);
  const dropdownRef = React.useRef<HTMLDivElement>(null);
  const [open, setOpen] = React.useState(false);
  const [dropdownPosition, setDropdownPosition] = React.useState({
    top: 0,
    left: 0,
    width: 0,
  });

  const primaryColor = useColorModeValue(theme.primary, theme.primary);
  const textColor = useColorModeValue(theme.text, theme.text);
  const borderColor = useColorModeValue(theme.disable, theme.disable);

  const currentLabel =
    sortOptions.find((o) => o.value === ordenarPor)?.label ??
    "Selecione uma opção";

  // ── Mesma lógica de posicionamento do InputSelect (considera modais) ──
  const calculateDropdownPosition = React.useCallback(() => {
    if (!wrapperRef.current) return;

    const rect = wrapperRef.current.getBoundingClientRect();
    const modalElement = wrapperRef.current.closest(
      '[role="dialog"], .chakra-modal__content, .modal, [data-testid="modal"]',
    );

    let calculatedLeft = rect.left;
    let calculatedTop = rect.bottom + window.scrollY;
    const calculatedWidth = rect.width;

    if (modalElement) {
      calculatedLeft = rect.left;
      calculatedTop = rect.bottom;

      const viewportWidth = window.innerWidth;
      if (calculatedLeft + calculatedWidth > viewportWidth) {
        calculatedLeft = viewportWidth - calculatedWidth - 10;
      }

      const viewportHeight = window.innerHeight;
      const dropdownHeight = 260;
      if (calculatedTop + dropdownHeight > viewportHeight) {
        calculatedTop = rect.top - dropdownHeight;
      }
    }

    setDropdownPosition({
      top: calculatedTop,
      left: Math.max(0, calculatedLeft),
      width: calculatedWidth,
    });
  }, []);

  React.useEffect(() => {
    if (!open) return;

    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node;
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(target)
      ) {
        setOpen(false);
      }
    }

    function handleScroll(event: Event) {
      const target = event.target as Element;
      if (
        dropdownRef.current &&
        (dropdownRef.current.contains(target) || dropdownRef.current === target)
      ) {
        return;
      }
      setOpen(false);
    }

    function handleResize() {
      calculateDropdownPosition();
    }

    const timeoutId = setTimeout(() => {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("scroll", handleScroll, true);
      window.addEventListener("resize", handleResize);
      calculateDropdownPosition();
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("scroll", handleScroll, true);
      window.removeEventListener("resize", handleResize);
    };
  }, [open, calculateDropdownPosition]);

  const handleOpenDropdown = () => {
    calculateDropdownPosition();
    setOpen((prev) => !prev);
  };

  const handleSelectField = (value: OrdenarPor) => {
    onChange(value, tipoOrdenacao);
    setTimeout(() => setOpen(false), 50);
  };

  const handleSelectDirection = (direction: TipoOrdenacao) => {
    onChange(ordenarPor, direction);
    setTimeout(() => setOpen(false), 50);
  };

  return (
    <Box ref={wrapperRef} width="100%" position="relative">
      <Text fontSize="12px" color={textColor} ml="3px">
        Ordenar por
      </Text>

      {/* Botão — mesmo padrão do InputSelect */}
      <Flex
        tabIndex={0}
        role="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        h="44px"
        align="center"
        justify="space-between"
        px={3}
        cursor="pointer"
        bg="white"
        borderRadius="8px"
        borderWidth="2px"
        borderColor={borderColor}
        _focus={{
          borderColor: primaryColor,
          borderWidth: "1.5px",
          boxShadow: `0 0 0 1px ${primaryColor}`,
        }}
        onClick={handleOpenDropdown}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleOpenDropdown();
          }
        }}
      >
        <Text
          color={theme.text}
          fontWeight="normal"
          fontSize="14px"
          isTruncated
          flex={1}
        >
          {currentLabel}
        </Text>

        <Flex
          w="fit-content"
          direction="column"
          align="flex-end"
          justify="center"
        >
          <Icon as={IoIosArrowUp} color={primaryColor} fontSize="sm" />
          <Icon as={IoIosArrowDown} color={primaryColor} fontSize="sm" />
        </Flex>
      </Flex>

      {open && (
        <Portal>
          <Box
            ref={dropdownRef}
            position="fixed"
            bg="white"
            borderRadius="8px"
            mt="1"
            border="1px solid"
            borderColor={borderColor}
            boxShadow="xl"
            zIndex={99999}
            display="flex"
            flexDirection="column"
            left={`${dropdownPosition.left}px`}
            top={`${dropdownPosition.top}px`}
            width={`${Math.max(dropdownPosition.width, 220)}px`}
            onClick={(e) => e.stopPropagation()}
            py="6px"
          >
            {sortOptions.map((option) => {
              const isSelected = option.value === ordenarPor;
              return (
                <Flex
                  key={option.value}
                  as="li"
                  role="option"
                  aria-selected={isSelected}
                  align="center"
                  justify="space-between"
                  px="12px"
                  py="8px"
                  cursor="pointer"
                  listStyleType="none"
                  _hover={{ bg: "#F7F9FC" }}
                  onClick={() => handleSelectField(option.value)}
                >
                  <Text
                    fontSize="14px"
                    fontWeight={isSelected ? "600" : "400"}
                    color={theme.text}
                  >
                    {option.label}
                  </Text>
                  {isSelected && (
                    <Icon as={FiCheck} color={theme.primary} fontSize="14px" />
                  )}
                </Flex>
              );
            })}

            <Box borderTop="1px solid" borderColor={borderColor} my="4px" />

            <Flex
              align="center"
              justify="space-between"
              px="12px"
              py="8px"
              cursor="pointer"
              _hover={{ bg: "#F7F9FC" }}
              onClick={() => handleSelectDirection("asc")}
            >
              <Flex align="center" gap="8px">
                <Text
                  fontSize="14px"
                  fontWeight={tipoOrdenacao === "asc" ? "600" : "400"}
                  color={theme.text}
                >
                  Crescente
                </Text>
                <Icon as={FiArrowDown} fontSize="12px" color={theme.text} />
              </Flex>
              {tipoOrdenacao === "asc" && (
                <Icon as={FiCheck} color={theme.primary} fontSize="14px" />
              )}
            </Flex>

            <Flex
              align="center"
              justify="space-between"
              px="12px"
              py="8px"
              cursor="pointer"
              _hover={{ bg: "#F7F9FC" }}
              onClick={() => handleSelectDirection("desc")}
            >
              <Flex align="center" gap="8px">
                <Text
                  fontSize="14px"
                  fontWeight={tipoOrdenacao === "desc" ? "600" : "400"}
                  color={theme.text}
                >
                  Decrescente
                </Text>
                <Icon as={FiArrowUp} fontSize="12px" color={theme.text} />
              </Flex>
              {tipoOrdenacao === "desc" && (
                <Icon as={FiCheck} color={theme.primary} fontSize="14px" />
              )}
            </Flex>
          </Box>
        </Portal>
      )}
    </Box>
  );
};

export default InputSortBy;
